import React from 'react';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import orderStatus, { type OrderStatus as OrderStatusType } from 'constants/orderStatus';
import {
  Clock,
  Pen,
  Van,
  Success,
  WarningTriangle,
  Warning,
  RemoveOutline,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { getStatus } from 'redux/modules/orders/selectors/get-order';
import {
  getDeliveryRunningLateByOrderId,
  getDeliveryStatusByOrderId,
} from 'redux/modules/delivery-tracking/selectors';
import deliveryStatusType, {
  type DeliveryStatus as DeliveryStatusType,
} from 'constants/deliveryStatus';
import { useWtrSelector } from 'redux/hooks';
import styles from './OrderStatus.scss';

export interface OrderStatusProps {
  orderId: string;
}

function getDisplayStatusAndIcon(
  status: OrderStatusType | undefined,
  orderDeliveryStatus: DeliveryStatusType | undefined,
  runningLate: boolean | undefined,
) {
  const orderIsPicked = status === orderStatus.PICKED || status === orderStatus.PAID;

  if (status === orderStatus.PAYMENT_FAILED) {
    return {
      status: 'PAYMENT FAILED',
      icon: (
        <Warning className={styles.errorIcon} data-testid="order-status-error-icon" size="medium" />
      ),
    };
  }

  if (orderDeliveryStatus === deliveryStatusType.DELIVERED || status === orderStatus.COMPLETED) {
    return {
      status: status === orderStatus.COMPLETED ? 'COMPLETED' : 'DELIVERED',
      icon: runningLate ? (
        <WarningTriangle
          className={styles.warningIcon}
          data-testid="order-status-warning-icon"
          size="medium"
        />
      ) : (
        <Success
          className={styles.completedIcon}
          data-testid="order-status-success-icon"
          size="medium"
        />
      ),
    };
  }

  if (status === orderStatus.AMENDING) {
    return {
      status: 'AMENDING',
      icon: (
        <Pen className={styles.amendingIcon} data-testid="order-status-pen-icon" size="medium" />
      ),
    };
  }

  if (orderIsPicked && orderDeliveryStatus === deliveryStatusType.PICKED) {
    return {
      status: 'PREPARING',
      icon: runningLate ? (
        <WarningTriangle
          className={styles.warningIcon}
          data-testid="order-status-warning-icon"
          size="medium"
        />
      ) : (
        <Van className={styles.vanIcon} data-testid="order-status-van-icon" size="medium" />
      ),
    };
  }

  if (orderIsPicked && orderDeliveryStatus === deliveryStatusType.OUT_FOR_DELIVERY) {
    return {
      status: 'OUT FOR DELIVERY',
      icon: runningLate ? (
        <WarningTriangle
          className={styles.warningIcon}
          data-testid="order-status-warning-icon"
          size="medium"
        />
      ) : (
        <Van className={styles.vanIcon} data-testid="order-status-van-icon" size="medium" />
      ),
    };
  }

  if (orderIsPicked && orderDeliveryStatus === deliveryStatusType.NEXT) {
    return {
      status: "YOU'RE NEXT!",
      icon: runningLate ? (
        <WarningTriangle
          className={styles.warningIcon}
          data-testid="order-status-warning-icon"
          size="medium"
        />
      ) : (
        <Van className={styles.vanIcon} data-testid="order-status-van-icon" size="medium" />
      ),
    };
  }

  if (orderDeliveryStatus === deliveryStatusType.MISSED) {
    return {
      status: 'WE MISSED YOU',
      icon: runningLate ? (
        <WarningTriangle
          className={styles.warningIcon}
          data-testid="order-status-warning-icon"
          size="medium"
        />
      ) : (
        <RemoveOutline
          className={styles.missedIcon}
          data-testid="order-status-missed-icon"
          size="medium"
        />
      ),
    };
  }

  if (status === orderStatus.CANCELLED) {
    return {
      status: 'CANCELLED',
      icon: (
        <RemoveOutline
          className={styles.cancelledIcon}
          data-testid="order-status-cancelled-icon"
          size="medium"
        />
      ),
    };
  }

  if (status === orderStatus.REFUND_PENDING) {
    return {
      status: 'REFUND PENDING',
      icon: (
        <Clock className={styles.pendingIcon} data-testid="order-status-clock-icon" size="medium" />
      ),
    };
  }

  return {
    status: 'PENDING',
    icon: (
      <Clock className={styles.pendingIcon} data-testid="order-status-clock-icon" size="medium" />
    ),
  };
}

const OrderStatus = ({ orderId }: OrderStatusProps) => {
  const status = useWtrSelector(state => getStatus(state, orderId));
  const orderDeliveryStatus = useWtrSelector(state => getDeliveryStatusByOrderId(state, orderId));
  const runningLate = useWtrSelector(state => getDeliveryRunningLateByOrderId(state, orderId));

  if (!status) {
    return null;
  }

  const displayItems = getDisplayStatusAndIcon(status, orderDeliveryStatus, runningLate);

  return (
    <div className={styles.orderStatus} data-testid="pending-order-status">
      {displayItems.icon}
      <Typography styleAs="paragraphExtraSmall" noMargins className={styles.orderStatusLabel}>
        {displayItems.status}
      </Typography>
    </div>
  );
};

export default OrderStatus;
